import {
    CategoryScale,
    Chart,
    ChartOptions,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { TFunction } from "i18next";
import * as React from "react";
import { Line } from "react-chartjs-2";
import { Translation } from "react-i18next";

import style from "./chart.scss";
import { commonFunctions, Units } from "./CommonFunctions";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { TextBlock } from "components/typography/textBlock/TextBlock";
import { DashboardDataList, DeviceType } from "domain/dashboard";
import { resolveColor } from "utils/commonFunctions";

interface ChartProps {
    displayLegend?: boolean;
    widgetData: DashboardDataList[];
    dataFetched: boolean;
    convertToTonnes: boolean;
}

interface ChartState {
    data: [];
    displayLegend?: boolean;
}

type Props = ChartProps;

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ChartDataLabels, Filler);

class EmissionsPreventedChart extends React.Component<Props, ChartState> {
    public static defaultProps = {
        displayLegend: false,
        convertToTonnes: false,
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            data: [],
            displayLegend: this.props.displayLegend,
        };
    }

    computeAverageCo2ProcessPerDay(): number {
        return parseInt(String(commonFunctions.computeTotalOfCo2EmissionsPrevented(this.props.widgetData) / 365));
    }

    fetchCo2Emissions = (deviceType: DeviceType) => {
        const co2Emissions: number[] = [];
        Object.values(this.props.widgetData).forEach((deviceData) => {
            Object.entries(deviceData.content).forEach(([key, value]) => {
                if (deviceType === key) {
                    co2Emissions.push(this.props.convertToTonnes ? value.co2 / 1000 : value.co2);
                }
            });
        });
        return co2Emissions;
    };

    public render(): JSX.Element {
        if (!this.props.dataFetched) {
            return (
                <div className={style.loaderContainer}>
                    <LoadingIndicator />
                </div>
            );
        }
        const displayedValuesAndUnits = commonFunctions.getDisplayedValueAndUnit(
            commonFunctions.computeTotalOfCo2EmissionsPrevented(this.props.widgetData),
            this.computeAverageCo2ProcessPerDay()
        );

        return (
            <Translation>
                {(t) => (
                    <div className={style.chartContainer}>
                        <div className={style.topSummaryContainer}>
                            <TextBlock>
                                <span className={style.summaryCount}>
                                    {t("EsgDashboardView.co2EmissionsPreventedChart.totalOfCo2EmissionsPrevented", {
                                        totalEmissionsPrevented: displayedValuesAndUnits.total.toFixed(),
                                        unit: t(displayedValuesAndUnits.totalUnit),
                                    })}
                                </span>
                                <br />
                                {t("EsgDashboardView.referencedMonths", {
                                    firstMonth: commonFunctions.computeFirstMonth(this.props.widgetData),
                                    lastMonth: commonFunctions.computeLastMonth(this.props.widgetData),
                                })}
                                <br />
                                {t("EsgDashboardView.averagePerDay", {
                                    averagePerDay: displayedValuesAndUnits.average?.toFixed(2),
                                    unit: displayedValuesAndUnits.averageUnit
                                        ? t(displayedValuesAndUnits.averageUnit)
                                        : "",
                                    numberOfMonths: commonFunctions.fetchMonths(this.props.widgetData).length,
                                })}
                            </TextBlock>
                        </div>
                        <Line
                            data={commonFunctions.getChartData(
                                t,
                                this.fetchCo2Emissions,
                                this.props.widgetData,
                                "line"
                            )}
                            options={getChartOptions(
                                resolveColor("--app-chart-gridLineColor"),
                                resolveColor("--app-chart-textColor"),
                                this.props.convertToTonnes,
                                displayedValuesAndUnits.totalUnit,
                                t
                            )}
                        />
                    </div>
                )}
            </Translation>
        );
    }
}

function getChartOptions(
    gridLineColor: string,
    textColor: string,
    convert: boolean,
    unit: Units,
    t: TFunction
): ChartOptions<"line"> {
    return {
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: textColor,
                },
            },
            y: {
                display: true,
                grid: {
                    color: gridLineColor,
                },
                beginAtZero: true,
                ticks: {
                    callback(n: number) {
                        return commonFunctions.toYAxisValueWithUnit(n, convert, unit, t(unit));
                    },
                    maxTicksLimit: 5,
                    color: textColor,
                },
                stacked: true,
            },
        },
        elements: {
            line: {
                tension: 0,
            },
            point: {
                radius: 1,
            },
        },
        plugins: {
            legend: {
                display: true,
                position: "bottom",
                labels: {
                    usePointStyle: true,
                    color: textColor,
                },
            },
            tooltip: {
                enabled: true,
            },
            datalabels: {
                display: false,
            },
        },
        responsive: true,
        maintainAspectRatio: false,
    };
}

export default EmissionsPreventedChart;
